import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define types for the location state
export interface DeliveryZone {
  documentId: string;
  name: string;
}

export interface LocationState {
  country: string;
  countryThumbnail: string;
  department: string;
  province: string;
  district: string;
  ubigeoCode: string;
  address: string;
  phoneNumber: string;
  name: string;
  lastname: string;
  // New zone arrays for array-based delivery zones
  deliveryFeeZones: DeliveryZone[];
  deliveryLeadTimeZones: DeliveryZone[];
  // Legacy compatibility - keep single zone fields for backward compatibility
  deliveryFeeZone: DeliveryZone | null;
  deliveryLeadTimeZone: DeliveryZone | null;
}

// Define location update payload types
export interface LocationUpdatePayload {
  country?: string;
  countryThumbnail?: string;
  department?: string;
  province?: string;
  district?: string;

  ubigeoCode?: string;
  address?: string;
  phoneNumber?: string;
  name?: string;
  lastname?: string;
  // New zone arrays for array-based delivery zones
  deliveryFeeZones?: DeliveryZone[];
  deliveryLeadTimeZones?: DeliveryZone[];
  // Legacy compatibility
  deliveryFeeZone?: DeliveryZone | null;
  deliveryLeadTimeZone?: DeliveryZone | null;
}

const initialState: LocationState = {
  country: "",
  countryThumbnail: "",
  department: "",
  province: "",
  district: "",
  ubigeoCode: "",
  address: "",
  phoneNumber: "",
  name: "",
  lastname: "",
  // New zone arrays for array-based delivery zones
  deliveryFeeZones: [],
  deliveryLeadTimeZones: [],
  // Legacy compatibility
  deliveryFeeZone: null, // { documentId: string, name: string }
  deliveryLeadTimeZone: null, // { documentId: string, name: string }
};

export const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setLocation: (state, action: PayloadAction<LocationState>) => {
      // Update state with the new location data
      state.country = action.payload.country;
      state.countryThumbnail = action.payload.countryThumbnail;
      state.department = action.payload.department;
      state.province = action.payload.province;
      state.district = action.payload.district;
      state.ubigeoCode = action.payload.ubigeoCode;
      state.address = action.payload.address;
      state.phoneNumber = action.payload.phoneNumber;
      state.name = action.payload.name;
      state.lastname = action.payload.lastname;

      // Add delivery zone fields - both arrays and legacy fields
      state.deliveryFeeZones = action.payload.deliveryFeeZones || [];
      state.deliveryLeadTimeZones = action.payload.deliveryLeadTimeZones || [];
      state.deliveryFeeZone = action.payload.deliveryFeeZone || null;
      state.deliveryLeadTimeZone = action.payload.deliveryLeadTimeZone || null;
    },
    updateLocation: (state, action: PayloadAction<LocationUpdatePayload>) => {
      Object.keys(action.payload).forEach((key) => {
        const typedKey = key as keyof LocationUpdatePayload;
        if (typedKey in state) {
          (state as any)[typedKey] = action.payload[typedKey];
        }
      });
    },
    clearLocation: (state) => {
      // Reset to initial state
      Object.keys(initialState).forEach((key) => {
        const typedKey = key as keyof LocationState;
        (state as any)[typedKey] = initialState[typedKey];
      });
    },
  },
});

export const { setLocation, updateLocation, clearLocation } =
  locationSlice.actions;
export default locationSlice.reducer;
